/**
 * Typed client for /api/announcements and /api/admin/announcements.
 *
 * Mirrors hy_sales/schemas/announcements.py — keep the types in lockstep
 * when the backend changes.
 *
 * The announcements themselves live in the database and are published by
 * an admin. They used to live in `src/data/announcements.ts`, which made
 * publishing a side effect of a deploy: no approval, no preview, and no
 * way to pull one back without shipping again.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';

/** How an announcement interrupts. */
export type Presentation = 'modal' | 'banner';

/** `draft` is admin-only. Only `published` is ever sent to a reader. */
export type AnnouncementStatus = 'draft' | 'published';

export interface AnnouncementAction {
  label: string;
  /** In-app router path, e.g. `/user-guide`. */
  to: string;
}

/**
 * The logo at the top of a modal. `hy` is the Hooten Young monogram (the
 * default); `ask_abhi` shows the Ask ABHI logo and a "where to find it" cue.
 */
export type AnnouncementMark = 'hy' | 'ask_abhi';

/** One announcement, as a reader sees it. */
export interface Announcement {
  key: string;
  title: string;
  body: string;
  action?: AnnouncementAction | null;
  presentation: Presentation;
  mark?: AnnouncementMark;
}

export interface AnnouncementsResponse {
  items: Announcement[];
}

/** One announcement, as an admin sees it — the whole row. */
export interface AdminAnnouncement {
  id: number;
  key: string;
  title: string;
  body: string;
  action_label: string | null;
  action_to: string | null;
  presentation: Presentation;
  mark: AnnouncementMark;
  status: AnnouncementStatus;
  published_on: string | null;
  expires_on: string | null;
  created_at: string;
  updated_at: string;
  ack_count: number;
}

export interface AdminAnnouncementsResponse {
  items: AdminAnnouncement[];
  /** Active accounts — the denominator for "14 of 22 have seen it". */
  audience: number;
}

export interface AnnouncementWrite {
  title: string;
  body: string;
  action_label: string | null;
  action_to: string | null;
  presentation: Presentation;
  mark: AnnouncementMark;
  published_on: string | null;
  expires_on: string | null;
}

const LIVE_KEY = ['announcements', 'live'] as const;
const ADMIN_KEY = ['announcements', 'admin'] as const;

/**
 * Every announcement that is live right now, newest first.
 *
 * Same `retry: false` reasoning as the acks below — if this fails the
 * user simply sees no announcement, which is far better than retrying on
 * every page load for something decorative.
 */
export function useLiveAnnouncements(enabled: boolean) {
  return useQuery({
    queryKey: LIVE_KEY,
    queryFn: () => apiGet<AnnouncementsResponse>('sales', '/api/announcements'),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export interface AnnouncementAcksResponse {
  acknowledged: string[];
}

/**
 * How the announcement was closed.
 *
 * Coarse on purpose: ×, Escape and a backdrop click are the same intent
 * expressed three ways, so they all land on `dismissed`. What is worth
 * telling apart is whether someone went and read the thing.
 */
export type AckVia = 'opened' | 'acknowledged' | 'dismissed';

export interface AnnouncementAckResponse {
  key: string;
  acknowledged_at: string;
  via: AckVia;
}

const ACKS_KEY = ['announcements', 'acks'] as const;

export function fetchAnnouncementAcks(): Promise<AnnouncementAcksResponse> {
  return apiGet<AnnouncementAcksResponse>('sales', '/api/announcements/acks');
}

export function acknowledgeAnnouncement(
  key: string,
  via: AckVia,
): Promise<AnnouncementAckResponse> {
  return apiPost<AnnouncementAckResponse>('sales', '/api/announcements/acks', { key, via });
}

/**
 * Which announcements this user has already dismissed.
 *
 * `retry: false` on purpose. This decides whether to show a banner; if
 * the call fails we simply show nothing rather than retrying on every
 * page load for a decoration. A long `staleTime` for the same reason —
 * the answer only changes when this user dismisses something, and we
 * update the cache ourselves when they do.
 */
export function useAnnouncementAcks(enabled: boolean) {
  return useQuery({
    queryKey: ACKS_KEY,
    queryFn: fetchAnnouncementAcks,
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Dismiss an announcement.
 *
 * The cache is updated optimistically and deliberately NOT rolled back
 * on failure: the person clicked dismiss, so the banner should go away
 * and stay away for this session even if the write did not land. Worst
 * case it reappears on their next sign-in, which is a far better outcome
 * than a banner that springs back under their cursor.
 */
export function useAcknowledgeAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, via }: { key: string; via: AckVia }) => acknowledgeAnnouncement(key, via),
    onMutate: ({ key }: { key: string; via: AckVia }) => {
      queryClient.setQueryData<AnnouncementAcksResponse>(ACKS_KEY, (prev) => ({
        acknowledged: [...(prev?.acknowledged ?? []), key],
      }));
    },
  });
}

// ---------------------------------------------------------------------
// Admin — writing and publishing
// ---------------------------------------------------------------------

/** Every announcement including drafts. Admin only. */
export function useAdminAnnouncements() {
  return useQuery({
    queryKey: ADMIN_KEY,
    queryFn: () => apiGet<AdminAnnouncementsResponse>('sales', '/api/admin/announcements'),
  });
}

/**
 * Invalidate both lists after any admin write.
 *
 * The live list matters as much as the admin one: an admin who
 * unpublishes something should stop seeing it themselves, and the
 * clearest proof it worked is their own copy going away.
 */
function useAnnouncementRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
    void queryClient.invalidateQueries({ queryKey: LIVE_KEY });
  };
}

export function useCreateAnnouncement() {
  const refresh = useAnnouncementRefresh();
  return useMutation({
    mutationFn: (body: AnnouncementWrite) =>
      apiPost<AdminAnnouncement>('sales', '/api/admin/announcements', body),
    onSuccess: refresh,
  });
}

export function useUpdateAnnouncement() {
  const refresh = useAnnouncementRefresh();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AnnouncementWrite }) =>
      apiPatch<AdminAnnouncement>('sales', `/api/admin/announcements/${id}`, body),
    onSuccess: refresh,
  });
}

export function useSetAnnouncementStatus() {
  const refresh = useAnnouncementRefresh();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: AnnouncementStatus }) =>
      apiPut<AdminAnnouncement>('sales', `/api/admin/announcements/${id}/status`, { status }),
    onSuccess: refresh,
  });
}

export function useDeleteAnnouncement() {
  const refresh = useAnnouncementRefresh();
  return useMutation({
    mutationFn: (id: number) => apiDelete<void>('sales', `/api/admin/announcements/${id}`),
    onSuccess: refresh,
  });
}

/** One person, and what they did with the announcement. */
export interface AnnouncementReader {
  user_id: string;
  name: string;
  email: string;
  /** null for someone who has not seen it yet. */
  acknowledged_at: string | null;
  via: AckVia | 'unknown' | null;
}

export interface AnnouncementReadersResponse {
  key: string;
  seen: AnnouncementReader[];
  pending: AnnouncementReader[];
  /** Counts per `via` — reading vs swatting away. */
  by_via: Record<string, number>;
}

/**
 * Who has seen one announcement and who has not.
 *
 * Only fetched when an admin opens the panel: it is a per-user list, and
 * loading it for every row of the list screen would be a lot of rows
 * nobody asked for.
 */
export function useAnnouncementReaders(id: number | null) {
  return useQuery({
    queryKey: ['announcements', 'readers', id],
    queryFn: () =>
      apiGet<AnnouncementReadersResponse>('sales', `/api/admin/announcements/${id}/readers`),
    enabled: id !== null,
  });
}
